/**
 * Notifications — the right rail's bell.
 *
 * Lives on the rail rather than the header because the rail ALREADY had an
 * Alerts bell (previously a disabled stub); adding a second one to the header
 * gave the app two bells meaning different things.
 *
 * Also the mechanism that makes an approval land in an ALREADY-OPEN TAB. Roles
 * are re-read from the store on every backend request, so the server knows the
 * moment an admin approves someone — but the SPA cached `/api/me` at boot. When
 * a notification arrives saying this user's trading access changed, we refetch
 * `/api/me` and the login button updates itself, with no sign-out.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import { useAppDispatch } from "@/state/hooks";
import { fetchMe } from "@/state/slices/meSlice";
import type { AppNotification } from "@/api/types";
import styles from "./NotificationBell.module.css";

const POLL_MS = 20000;

/** Violet reads as "message waiting" and keeps the rail's icons distinct;
 *  amber is reserved for the unread badge so it stands out against it. */
export const NOTIFICATIONS_COLOR = "#a78bfa";

/** Events that change what this user may do — they invalidate cached caps. */
const CAPABILITY_EVENTS = new Set([
  "trader_approved",
  "trader_revoked",
  "trader_ready",
]);

/** Notification ids already accounted for, kept at MODULE scope on purpose.
 *
 * `fetchMe.pending` sets `me.status = "loading"`, and App renders a splash for
 * anything other than "succeeded" — so a refetch UNMOUNTS the whole tree. A
 * component-level ref would therefore be wiped by the very refetch it
 * triggered, the remounted bell would see the same notification as new, and
 * dispatch again: a self-sustaining loop that re-ran every boot fetch about
 * once a second. Measured on dev, 2026-08-30. Module scope survives the
 * remount and breaks the cycle. */
const seenIds = new Set<string>();
/** False until the first poll of the session has been absorbed. That first
 *  batch must never trigger a refetch: App has just fetched `/api/me` anyway,
 *  so there is nothing to refresh — only notifications arriving LATER mean
 *  something actually changed. */
let primed = false;

const BellIcon = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M8 2 C6 2 5 3 5 5.5 V8 L3.5 10 H12.5 L11 8 V5.5 C11 3 10 2 8 2 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 11.5 C6.5 12.5 7.2 13 8 13 C8.8 13 9.5 12.5 9.5 11.5"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

function ago(iso: string): string {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function NotificationsRailButton() {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.notifications();
      setItems(res.notifications);
      setUnread(res.unread);
      let stale = false;
      for (const n of res.notifications) {
        if (seenIds.has(n.id)) continue;
        seenIds.add(n.id);
        if (primed && CAPABILITY_EVENTS.has(String(n.meta?.event ?? ""))) {
          stale = true;
        }
      }
      primed = true;
      if (stale) void dispatch(fetchMe());
    } catch {
      // A failed poll is not worth surfacing; the next one will do.
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load();
  };

  const markRead = async (id: string) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((n) => Math.max(0, n - 1));
    try {
      await api.notificationRead(id);
    } catch {
      /* optimistic; the next poll reconciles */
    }
  };

  const markAll = async () => {
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      await api.notificationsReadAll();
    } catch {
      /* optimistic */
    }
  };

  /** Admin convenience: copy the ready-to-send approval email. */
  const copyEmail = async (n: AppNotification) => {
    const body = String(n.meta?.body ?? "");
    if (!body) return;
    try {
      await navigator.clipboard.writeText(
        `To: ${String(n.meta?.to ?? "")}\nSubject: ${String(
          n.meta?.subject ?? "",
        )}\n\n${body}`,
      );
      setCopied(n.id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard unavailable (insecure origin) — the body is on screen */
    }
  };

  return (
    <div className={styles.railWrap} ref={boxRef}>
      <button
        type="button"
        className={`${styles.railBtn} ${open ? styles.railBtnActive : ""}`}
        title={unread ? `Notifications (${unread} unread)` : "Notifications"}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        aria-pressed={open}
        onClick={toggle}
        style={{ ["--icon-color" as string]: NOTIFICATIONS_COLOR }}
      >
        <BellIcon />
        {unread > 0 && (
          <span className={styles.railBadge}>{unread > 9 ? "9+" : unread}</span>
        )}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <header className={styles.head}>
            <span className={styles.title}>Notifications</span>
            {unread > 0 && (
              <button className={styles.link} onClick={markAll}>
                Mark all read
              </button>
            )}
          </header>

          <div className={styles.list}>
            {items.length === 0 && <p className={styles.empty}>Nothing yet.</p>}
            {items.map((n) => (
              <article
                key={n.id}
                className={`${styles.item} ${n.read ? "" : styles.unreadItem} ${
                  styles[n.kind] ?? ""
                }`}
                onClick={() => !n.read && void markRead(n.id)}
              >
                <div className={styles.itemHead}>
                  <span className={styles.itemTitle}>{n.title}</span>
                  <span className={styles.time}>{ago(n.created_at)}</span>
                </div>
                <pre className={styles.itemBody}>{n.body}</pre>
                {Boolean(n.meta?.body) && (
                  <div className={styles.actions}>
                    <button
                      className={styles.copy}
                      onClick={(e) => {
                        e.stopPropagation();
                        void copyEmail(n);
                      }}
                    >
                      {copied === n.id ? "Copied ✓" : "Copy email"}
                    </button>
                    {Boolean(n.meta?.needs_manual_send) && (
                      <span className={styles.flag}>needs sending</span>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
